'use client';

import React, { useRef } from 'react';
import gsap from 'gsap';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useGsapContext } from 'hooks/animation/useGsapContext';

type SplitTypeOption =
  | 'chars'
  | 'words'
  | 'lines'
  | 'chars,words'
  | 'chars,lines'
  | 'words,lines'
  | 'chars,words,lines';

type SplitTextProps = {
  text: string;
  className?: string;
  delay?: number;
  duration?: number;
  ease?: gsap.TweenVars['ease'];
  splitType?: SplitTypeOption;
  from?: gsap.TweenVars;
  to?: gsap.TweenVars;
  threshold?: number;
  rootMargin?: string;
  textAlign?: React.CSSProperties['textAlign'];
  tag?: React.ElementType;
  onLetterAnimationComplete?: () => void;
} & React.HTMLAttributes<HTMLElement>;

const SplitText: React.FC<SplitTextProps> = ({
  text,
  className = '',
  delay = 100,
  duration = 0.6,
  ease = 'power3.out',
  splitType = 'chars',
  from = { opacity: 0, y: 40 },
  to = { opacity: 1, y: 0 },
  threshold = 0.1,
  rootMargin = '-100px',
  textAlign = 'center',
  tag = 'p',
  onLetterAnimationComplete,
  ...rest
}: SplitTextProps) => {
  const ref = useRef<HTMLElement | null>(null);
  const gsapContext = useGsapContext(ref);

  useIsomorphicLayoutEffect(() => {
    if (!ref.current || !text) return;

    gsapContext.add(() => {
      if (!ref.current) return;

      const element = ref.current;

      let selector = '.split-char';
      if (splitType.includes('words')) selector = '.split-word';
      if (splitType.includes('lines')) selector = '.split-line';

      const targets = Array.from(
        element.querySelectorAll<HTMLElement>(selector),
      );

      if (!targets.length) return;

      const startPct = (1 - threshold) * 100;
      const marginMatch = /^(-?\d+(?:\.\d+)?)(px|em|rem|%)?$/.exec(rootMargin);
      const marginValue = marginMatch ? parseFloat(marginMatch[1]) : 0;
      const marginUnit = marginMatch ? marginMatch[2] || 'px' : 'px';

      const sign =
        marginValue === 0
          ? ''
          : marginValue < 0
          ? `-=${Math.abs(marginValue)}${marginUnit}`
          : `+=${marginValue}${marginUnit}`;

      const start = `top ${startPct}%${sign}`;

      gsap.fromTo(
        targets,
        { ...from },
        {
          ...to,
          duration,
          ease,
          stagger: delay / 1000,
          scrollTrigger: {
            trigger: element,
            start,
            once: true,
            fastScrollEnd: true,
            anticipatePin: 0.4,
          },
          onComplete: () => {
            onLetterAnimationComplete?.();
          },
          willChange: 'transform, opacity',
          force3D: true,
        },
      );
    });
  }, [
    text,
    delay,
    duration,
    ease,
    splitType,
    JSON.stringify(from),
    JSON.stringify(to),
    threshold,
    rootMargin,
    onLetterAnimationComplete,
    gsapContext,
  ]);

  const style: React.CSSProperties = {
    textAlign,
    overflow: 'hidden',
    display: 'inline-block',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    willChange: 'transform, opacity',
    fontFamily: 'inherit',
  };

  const TagComponent = (tag || 'p') as any;

  const tokens = text.split(/(\s+)/);

  return (
    <TagComponent
      ref={ref as React.Ref<HTMLElement>}
      style={style}
      className={`split-parent ${className || ''}`.trim()}
      aria-label={text}
      {...(rest as any)}
    >
      {tokens.map((token, tokenIndex) => {
        if (/^\s+$/.test(token)) {
          return token;
        }

        const chars = token.split('');

        return (
          <span
            key={`word-${token}-${tokenIndex}`}
            className="split-word"
            style={{
              fontFamily: 'inherit',
              display: 'inline-block',
            }}
            aria-hidden="true"
          >
            {chars.map((char, charIndex) => (
              <span
                key={`char-${tokenIndex}-${charIndex}`}
                className="split-char"
                style={{
                  fontFamily: 'inherit',
                  display: 'inline-block',
                }}
                aria-hidden="true"
              >
                {char}
              </span>
            ))}
          </span>
        );
      })}
    </TagComponent>
  );
};

export default SplitText;
